import type { GeoPoint, Place } from "@/lib/api/catalogue";
import {
    AREA_WITHIN_MILES,
    DEFAULT_AREA_WITHIN_MILES,
    type AreaSource,
    type AreaWithinMiles,
    type EnvoyAreaRequest,
} from "@/lib/api/envoy";
import { countryName } from "@/lib/openings/places";

/**
 * The envoy's area on its page: one city and how far from it. How an
 * answer is read, how it reads on the card, and what the editor saves.
 * Pure.
 */

/** The area card's id, for a line elsewhere on the page that points to it. */
export const AREA_CARD_ID = "envoy-area";

/** The most characters envoy keeps in a city's name. */
export const MAX_CITY_CHARS = 100;

/** The most miles the catalogue measures a search over. */
const MOST_MILES = 250;

/** The area as the page holds it, read from envoy's answer. */
export interface KnownArea {
    /** As a person reads it, such as `Denver, CO`. */
    city: string;
    country: string;
    region: string | null;
    /** Where the city is. Null when the answer's point is not one: nothing is measured from it. */
    point: GeoPoint | null;
    /** How far from the city, in whole miles. Null when the answer's is not a distance. */
    miles: number | null;
    by: AreaSource;
}

/** A city the editor can save: named, in a country, and on the map. */
export interface AreaPick {
    city: string;
    country: string;
    region: string | null;
    point: GeoPoint;
}

const record = (value: unknown): Record<string, unknown> | null =>
    typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;

/** A point on the map, or null for anything else: finite degrees within range. */
export function pointOf(value: unknown): GeoPoint | null {
    const fields = record(value);
    if (!fields) return null;
    const { latitude, longitude } = fields;
    if (typeof latitude !== "number" || typeof longitude !== "number") return null;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
    return { latitude, longitude };
}

/** A city's name as envoy keeps it: trimmed, one to 100 characters, no control characters. */
export function cityName(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const city = value.trim();
    if (city === "" || Array.from(city).length > MAX_CITY_CHARS) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f-\u009f]/.test(city)) return null;
    return city;
}

function countryOf(value: unknown): string | null {
    return typeof value === "string" && /^[a-z]{2}$/i.test(value.trim())
        ? value.trim().toUpperCase()
        : null;
}

function regionOf(value: unknown, country: string): string | null {
    if (typeof value !== "string") return null;
    const region = value.trim().toUpperCase();
    return /^[A-Z]{2}-[A-Z0-9]{1,3}$/.test(region) && region.startsWith(`${country}-`)
        ? region
        : null;
}

/**
 * Envoy's answer as the page holds it, or null when it says no city: none
 * at all, or an area kept before there were cities (a country and a state
 * only). A point or distance that is not one is kept as null, so the card
 * still names the city but nothing is measured from it.
 */
export function readArea(value: unknown): KnownArea | null {
    const fields = record(value);
    if (!fields) return null;
    const city = cityName(fields.city);
    const country = countryOf(fields.country);
    if (city === null || country === null) return null;
    const miles = fields.within_miles;
    return {
        city,
        country,
        region: regionOf(fields.region, country),
        point: pointOf(fields.point),
        miles:
            typeof miles === "number" &&
            Number.isInteger(miles) &&
            miles >= 1 &&
            miles <= MOST_MILES
                ? miles
                : null,
        by: fields.by === "PERSON" ? "PERSON" : "EDGE",
    };
}

/** The city on the card: as kept, with its country where its name does not say one. */
export function cityLine(area: Pick<KnownArea, "city" | "country" | "region">): string {
    return area.region || area.city.includes(",")
        ? area.city
        : `${area.city}, ${countryName(area.country)}`;
}

/** "within 25 mi of Denver, CO", or "near Denver, CO" when the distance is not known. */
export function withinLine(area: KnownArea): string {
    return area.miles === null
        ? `near ${cityLine(area)}`
        : `within ${area.miles} mi of ${cityLine(area)}`;
}

/** The card's line: "Within 25 mi of Denver, CO". */
export function areaLine(area: KnownArea): string {
    const line = withinLine(area);
    return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The distance the editor starts on: the one kept, else the nearest it offers, else 25. */
export function nearestMiles(miles: number | null | undefined): AreaWithinMiles {
    if (typeof miles !== "number" || !Number.isFinite(miles)) return DEFAULT_AREA_WITHIN_MILES;
    return AREA_WITHIN_MILES.reduce((best, choice) =>
        Math.abs(choice - miles) < Math.abs(best - miles) ? choice : best,
    );
}

/** Whether `miles` is one of the distances envoy keeps. */
export function isAreaMiles(miles: unknown): miles is AreaWithinMiles {
    return (AREA_WITHIN_MILES as readonly unknown[]).includes(miles);
}

/** A catalogue place the editor can save, or null: a city with a country and a point. */
export function pickOf(place: Place): AreaPick | null {
    if (place.level !== "CITY") return null;
    const city = cityName(place.value);
    const country = countryOf(place.country);
    const point = pointOf(place.point);
    if (city === null || country === null || point === null) return null;
    return { city, country, region: regionOf(place.region, country), point };
}

/** The area kept, as a pick the editor starts on, or null when it cannot be saved again. */
export function pickOfArea(area: KnownArea | null): AreaPick | null {
    if (!area || !area.point) return null;
    return { city: area.city, country: area.country, region: area.region, point: area.point };
}

/** The cities the picker offers: each once, and only the ones it can save. */
export function pickableCities(places: readonly Place[]): AreaPick[] {
    const seen = new Set<string>();
    const picks: AreaPick[] = [];
    for (const place of places) {
        const pick = pickOf(place);
        if (!pick || seen.has(pick.city)) continue;
        seen.add(pick.city);
        picks.push(pick);
    }
    return picks;
}

/** What the editor saves, or null when the city or the distance cannot be. */
export function areaRequest(pick: AreaPick | null, miles: number): EnvoyAreaRequest | null {
    if (!pick || !isAreaMiles(miles)) return null;
    const city = cityName(pick.city);
    const country = countryOf(pick.country);
    const point = pointOf(pick.point);
    if (city === null || country === null || point === null) return null;
    return {
        city,
        country,
        region: regionOf(pick.region, country),
        point,
        within_miles: miles,
    };
}

/** The point a search is measured from, and how far: only for a city on the map at a known distance. */
export function areaSearch(
    area: KnownArea | null,
): { within_miles: number; near_latitude: number; near_longitude: number } | null {
    if (!area || !area.point || area.miles === null) return null;
    return {
        within_miles: area.miles,
        near_latitude: area.point.latitude,
        near_longitude: area.point.longitude,
    };
}
